import { useState, useEffect } from 'react';
import { Node, Edge } from 'reactflow';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ElectricalSchemaFlow } from '../components/charts/ElectricalSchemaFlow';
import type { OcppBorne } from '../components/charts/ElectricalSchemaFlow';
import { ConsultationSchemaView, type ConsultationViewMode } from '../components/charts/ConsultationSchemaView';
import { ElectricalToolbox } from '../components/charts/ElectricalToolbox';
import { TemplatesSelector } from '../components/charts/TemplatesSelector';
import { SaveInstallationModal } from '../components/SaveInstallationModal';
import { tableauDisplayName } from '../components/charts/TableauElectriqueModal';
import { displayDefaultName } from '../i18n/defaultNames';
import { Btn, IconTile, LegendChip } from '../components/ui/Button';
import { IconPlus, IconEye, IconTrash, IconTemplate, IconSave, IconRotate, IconBolt, IconZone, IconPlug, IconCircleCheck, IconMeter, IconPanel, IconSliders, IconAlertTriangle } from '../components/ui/icons';
import { architectureApi, ApiStatusError, type MeterLatest, InstallationData, DeviceState, CatalogDevice } from '../api/architecture';
import { wattelEMApi, type TelemetryData } from '../api/wattelEM';
import { getOrgBornes } from '../api/bornesService';
import { otaApi } from '../api/ota';
import { displaySn } from '../utils/deviceDisplay';
import { useI18n } from '../i18n';

// Tableau type lookup for restoring metadata from name
const TABLEAU_TYPES_MAP: Record<string, { modules: number; rangees: number; marque: string }> = {
  'Tableau 1 rangée': { modules: 13, rangees: 1, marque: 'Schneider Resi9' },
  'Tableau 2 rangées': { modules: 26, rangees: 2, marque: 'Schneider Resi9' },
  'Tableau 3 rangées': { modules: 39, rangees: 3, marque: 'Schneider Resi9' },
  'Tableau 4 rangées': { modules: 52, rangees: 4, marque: 'Schneider Resi9' },
  'Drivia 2 rangées': { modules: 26, rangees: 2, marque: 'Legrand Drivia' },
  'Drivia 3 rangées': { modules: 39, rangees: 3, marque: 'Legrand Drivia' },
  'Drivia 4 rangées': { modules: 52, rangees: 4, marque: 'Legrand Drivia' },
  'Gamma 2 rangées': { modules: 26, rangees: 2, marque: 'Hager Gamma' },
  'Gamma 3 rangées': { modules: 39, rangees: 3, marque: 'Hager Gamma' },
  'Sous-tableau': { modules: 13, rangees: 1, marque: 'Modulaire' },
  'Coffret extérieur': { modules: 8, rangees: 1, marque: 'Étanche IP65' },
  'Tableau 1 disjoncteur': { modules: 6, rangees: 1, marque: 'Modulaire' },
};

// Convert DB installation to ReactFlow nodes & edges
function installationToFlow(inst: InstallationData, devices: DeviceState[]): { nodes: Node[]; edges: Edge[] } {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  // Default Linky compteur (always present as constant UI element)
  nodes.push({
    id: 'linky',
    type: 'linky',
    position: { x: 50, y: 250 },
    data: { label: 'Linky', type: 'Compteur', power: '12 kVA', icon: '📊', color: 'yellow' },
  });
  // Default Tableau électrique
  nodes.push({
    id: 'tableau',
    type: 'tableau',
    position: { x: 250, y: 250 },
    data: { label: 'Tableau Électrique', modules: 24 },
  });
  // Edge: Linky → Tableau
  edges.push({
    id: 'e-linky-tableau',
    source: 'linky',
    target: 'tableau',
    type: 'smoothstep',
    style: { stroke: '#666', strokeWidth: 3 },
  });

  // Build a map of device states by SN for fast lookup
  const deviceMap = new Map(devices.map(d => [d.deviceSn, d]));

  // Separate tableau-zones (name starts with "Tableau") from regular zones
  const tableauZones = inst.zones.filter(z => z.name.startsWith('Tableau'));
  const regularZones = inst.zones.filter(z => !z.name.startsWith('Tableau'));

  // Set of WattelEM SNs physically in the tableau — these are the SNs that have a
  // render handle "wem-<sn>" on the TableauNode. Used to wire zone edges to the right WEM.
  const tableauWemSns = new Set<string>();
  tableauZones.forEach(tz => {
    (tz.wattelemSn || '').split(',').map(s => s.trim()).filter(Boolean).forEach(sn => tableauWemSns.add(sn));
  });

  // Merge tableau-zones back into the default Tableau node
  if (tableauZones.length > 0) {
    const tz = tableauZones[0]; // primary tableau zone
    // Build wattelemList from the zone's wattelemSn (can be comma-separated for multiple)
    const wattelemList: any[] = [];
    if (tz.wattelemSn) {
      // Parse comma-separated SNs
      const snList = tz.wattelemSn.split(',').map(s => s.trim()).filter(Boolean);
      snList.forEach(sn => {
        const dev = deviceMap.get(sn);
        wattelemList.push({
          sn: sn,
          name: dev?.deviceSn || sn,
          power: Number(dev?.powerKw) || 0,
          status: dev ? (dev.online ? 'online' : 'offline') : 'offline',
        });
      });
    }
    // Update the default tableau node with restored data
    const tableauNode = nodes.find(n => n.id === 'tableau');
    if (tableauNode) {
      const tableauMeta = TABLEAU_TYPES_MAP[tz.name] || {};
      tableauNode.data = { ...tableauNode.data, label: tz.name, wattelemList, dbId: tz.id, ...tableauMeta };
      tableauNode.position = { x: tz.positionX || 250, y: tz.positionY || 250 };
    }

    // Equipment connected to the tableau
    tz.equipment.forEach((eq) => {
      const eqNodeId = `eq-${eq.id}`;
      nodes.push({
        id: eqNodeId,
        type: 'point',
        position: { x: eq.positionX || 700, y: eq.positionY || 0 },
        data: {
          label: eq.name,
          icon: eq.icon || '🔌',
          type: eq.type,
          power: Number(eq.power) || 0,
          status: 'online',
          dbId: eq.id,
          deviceId: eq.deviceId || null,
          deviceSn: eq.deviceSn || null,
          wattelDeviceType: eq.wattelDeviceType || null,
        },
      });

      // Edge: Tableau → Equipment
      edges.push({
        id: `e-tableau-${eqNodeId}`,
        source: 'tableau',
        target: eqNodeId,
        type: 'smoothstep',
        style: { stroke: '#ec4899', strokeWidth: 2 },
      });
    });
  }

  // Regular zones (non-tableau) + their equipment
  regularZones.forEach((zone) => {
    const zoneNodeId = `zone-${zone.id}`;
    const device = zone.wattelemSn ? deviceMap.get(zone.wattelemSn) : undefined;

    nodes.push({
      id: zoneNodeId,
      type: 'zone',
      position: { x: zone.positionX || 480, y: zone.positionY || 50 },
      data: {
        label: zone.name,
        icon: zone.icon || '⚡',
        power: Number(device?.powerKw) || 0,
        status: device ? (device.online ? 'online' : 'offline') : 'offline',
        wattelemSn: zone.wattelemSn,
        dbId: zone.id,
      },
    });

    // Edge: Tableau → Zone
    // Wire the edge to the specific WattelEM handle ("wem-<sn>") that powers this zone,
    // so the connection renders identically in edit and view (reloaded) modes.
    const zoneWemSn = zone.wattelemSn && tableauWemSns.has(zone.wattelemSn) ? zone.wattelemSn : undefined;
    edges.push({
      id: `e-tableau-${zoneNodeId}`,
      source: 'tableau',
      sourceHandle: zoneWemSn ? `wem-${zoneWemSn}` : undefined,
      target: zoneNodeId,
      type: 'smoothstep',
      style: { stroke: '#0891b2', strokeWidth: 2 },
    });

    // Equipment inside this zone
    zone.equipment.forEach((eq) => {
      const eqNodeId = `eq-${eq.id}`;
      nodes.push({
        id: eqNodeId,
        type: 'point',
        position: { x: eq.positionX || 700, y: eq.positionY || 0 },
        data: {
          label: eq.name,
          icon: eq.icon || '🔌',
          type: eq.type,
          power: Number(eq.power) || 0,
          status: 'online',
          dbId: eq.id,
          deviceId: eq.deviceId || null,
          deviceSn: eq.deviceSn || null,
          wattelDeviceType: eq.wattelDeviceType || null,
        },
      });

      // Edge: Zone → Equipment
      edges.push({
        id: `e-${zoneNodeId}-${eqNodeId}`,
        source: zoneNodeId,
        target: eqNodeId,
        type: 'smoothstep',
        style: { stroke: '#ec4899', strokeWidth: 2 },
      });
    });
  });

  return { nodes, edges };
}

// Extract zones & equipment data from ReactFlow nodes for saving to API
// Tableau nodes are also saved as zones (the DB doesn't differentiate)
function flowToInstallationData(nodes: Node[], edges: Edge[]) {
  // Include both zone and tableau nodes — both save as DB zones
  const zoneNodes = nodes.filter(n => n.type === 'zone' || n.type === 'tableau');
  const pointNodes = nodes.filter(n => n.type === 'point');

  // Build parent map from edges: target → source (and capture the source handle)
  const parentMap = new Map<string, string>();
  const sourceHandleMap = new Map<string, string>();
  edges.forEach(e => {
    parentMap.set(e.target, e.source);
    if (e.sourceHandle) sourceHandleMap.set(e.target, e.sourceHandle);
  });

  return zoneNodes.map(zn => {
    // Find equipment connected to this zone/tableau
    const children = pointNodes.filter(pn => parentMap.get(pn.id) === zn.id);

    // For tableau nodes, extract ALL WattelEM SNs from wattelemList (joined by comma)
    // For zone nodes, the WattelEM is determined by which WEM handle the zone is wired to
    // (sourceHandle "wem-<sn>"). This keeps edit and view modes consistent.
    let wattelemSn: string | undefined = undefined;
    if (zn.type === 'tableau') {
      const wattelemList = zn.data?.wattelemList || [];
      const sns = wattelemList.map((w: { sn?: string }) => w.sn).filter(Boolean);
      wattelemSn = sns.length > 0 ? sns.join(',') : undefined;
    } else {
      // Source of truth: the WEM handle wired to this zone (visual connection)
      const sh = sourceHandleMap.get(zn.id);
      if (sh && sh.startsWith('wem-')) {
        wattelemSn = sh.slice(4); // strip "wem-" prefix
      } else {
        wattelemSn = zn.data?.wattelemSn || undefined;
      }
    }

    return {
      name: zn.data?.label || (zn.type === 'tableau' ? 'Tableau Électrique' : 'Zone'),
      icon: zn.data?.icon || (zn.type === 'tableau' ? '⚡' : undefined),
      wattelemSn,
      positionX: zn.position.x,
      positionY: zn.position.y,
      isTableau: zn.type === 'tableau', // marker for reload identification
      equipment: children.map(ch => ({
        name: ch.data?.label || 'Equipment',
        icon: ch.data?.icon,
        type: ch.data?.type,
        power: ch.data?.power ?? null,
        deviceId: ch.data?.deviceId ?? null,
        deviceSn: ch.data?.deviceSn ?? null,
        wattelDeviceType: ch.data?.wattelDeviceType || null,
        positionX: ch.position.x,
        positionY: ch.position.y,
      })),
    };
  });
}

export function BuildingArchitecturePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { hasOrgRole } = useAuth();
  const { t, tp, locale } = useI18n();
  const canEdit = hasOrgRole('org_admin', 'maintenance'); // simple 'user' role is read-only
  const requestedMode = searchParams.get('mode') || 'view';
  // Force view mode for simple users even if they manually set ?mode=edit
  const mode = canEdit ? requestedMode : 'view';
  const loadId = searchParams.get('load');
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  // Consultation : Schéma (ReactFlow lecture seule) ou Synoptique (flux d'énergie). Mémorisé par navigateur.
  const [viewMode, setViewMode] = useState<ConsultationViewMode>(() => {
    try { return localStorage.getItem('zos-architecture-view') === 'synoptic' ? 'synoptic' : 'schema'; } catch { return 'schema'; }
  });
  const changeViewMode = (m: ConsultationViewMode) => {
    setViewMode(m);
    try { localStorage.setItem('zos-architecture-view', m); } catch { /* stockage indisponible */ }
  };
  const [currentNodes, setCurrentNodes] = useState<Node[]>([]);
  const [currentEdges, setCurrentEdges] = useState<Edge[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isToolboxOpen, setIsToolboxOpen] = useState(true);
  const [showTemplates, setShowTemplates] = useState(false);
  const [isVerticalView, setIsVerticalView] = useState(false); // Toggle pour vue verticale
  const [schemaKey, setSchemaKey] = useState(0);
  const [currentInstallationId, setCurrentInstallationId] = useState<number | null>(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [devices, setDevices] = useState<DeviceState[]>([]);
  const [catalogDevices, setCatalogDevices] = useState<CatalogDevice[]>([]);
  const [ocppBornes, setOcppBornes] = useState<OcppBorne[]>([]);
  const [meterData, setMeterData] = useState<MeterLatest[]>([]);
  const [wattelemTelemetry, setWattelemTelemetry] = useState<Record<string, TelemetryData>>({});
  const [currentInstallationName, setCurrentInstallationName] = useState('');
  const [currentInstallationDesc, setCurrentInstallationDesc] = useState('');
  const [installations, setInstallations] = useState<InstallationData[]>([]);
  const [showNewInstallationModal, setShowNewInstallationModal] = useState(false);

  // Force re-render when mode or loadId changes (helps with mode=view -> mode=edit transitions)
  useEffect(() => {
    setSchemaKey(prev => prev + 1);
  }, [mode, loadId]);

  // Load installations list on mount
  useEffect(() => {
    architectureApi.getInstallations()
      .then(res => setInstallations(res.data || []))
      .catch(err => console.error('[Architecture] Failed to load installations list:', err));
  }, []);

  // Load WATTELEM device states on mount
  useEffect(() => {
    console.log('[Architecture] Loading WATTELEM devices...');
    architectureApi.getDevices()
      .then(res => {
        console.log('[Architecture] Devices loaded:', res.data?.length, res.data);
        setDevices(res.data || []);
      })
      .catch(err => {
        console.error('[Architecture] Failed to load devices:', err);
        setSaveMessage({ type: 'error', text: t('arch_err_load_wattelem', { msg: err.message }) });

      });
    // Load catalog devices (WattelGo, WattelWall, etc.)
    architectureApi.getCatalogDevices()
      .then(res => {
        console.log('[Architecture] Catalog devices loaded:', res.data?.length);
        setCatalogDevices(res.data || []);
      })
      .catch(err => {
        console.error('[Architecture] Failed to load catalog devices:', err);
      });

    // Load OCPP bornes (Wattel 22, WattelGo, WattelWall...) depuis le device-service
    // (numéros de série en base OCPP) + noms personnalisés depuis org_bornes.
    // Le device_id numérique permet la navigation ?deviceId= → mode catalogue réel
    // (tension/puissance/énergie temps réel + on/off + graphe de consommation).
    Promise.all([
      otaApi.getDevices().catch(() => ({ success: false, data: [] as any[] })),
      getOrgBornes().catch(() => ({ success: false, data: [] as any[] })),
    ])
      .then(([otaRes, orgRes]) => {
        const otaList: any[] = (otaRes as any)?.data || [];
        const orgList: any[] = (orgRes as any)?.data || [];
        const nameBySn = new Map<string, string>();
        orgList.forEach((b: any) => { if (b?.device_sn) nameBySn.set(String(b.device_sn).toLowerCase(), b.borne_name); });
        const modelOf = (sn: string): string => {
          const s = (sn || '').toLowerCase();
          if (s.includes('go')) return 'WattelGo';
          if (s.includes('wall') || s.includes('-wl-') || s.includes('wl')) return 'WattelWall';
          return 'Borne';
        };
        const bornes: OcppBorne[] = otaList
          .filter(d => d && d.device_id != null && d.device_sn)
          .map(d => {
            const sn = String(d.device_sn);
            const status = String(d.device_status || '').toLowerCase();
            return {
              deviceId: Number(d.device_id),
              deviceSn: sn,
              deviceName: nameBySn.get(sn.toLowerCase()) || d.device_name || sn,
              model: modelOf(sn),
              online: status === 'available' || status === 'active' || status === 'connected' || status === 'preparing' || status === 'charging',
            };
          });
        console.log('[Architecture] OCPP bornes loaded:', bornes.length, bornes.map(b => `${b.deviceName}(${b.deviceId})`));
        setOcppBornes(bornes);
      })
      .catch(err => console.error('[Architecture] Failed to load OCPP bornes:', err));
  }, []);

  // Poll latest meter values for devices assigned to points (every 20s)
  useEffect(() => {
    const fetchMeter = () => {
      const allPoints = currentNodes.filter(n => n.type === 'point');
      console.log('[MeterPoll] All point nodes:', allPoints.map(n => ({ id: n.id, label: n.data?.label, deviceId: n.data?.deviceId })));
      
      const deviceIds = currentNodes
        .filter(n => n.type === 'point' && n.data?.deviceId)
        .map(n => n.data.deviceId as number);
      console.log('[MeterPoll] deviceIds found:', deviceIds);
      if (deviceIds.length === 0) return;
      const uniqueIds = [...new Set(deviceIds)];
      console.log('[MeterPoll] Fetching meter values for:', uniqueIds);
      architectureApi.getLatestMeterValues(uniqueIds)
        .then(res => {
          console.log('[MeterPoll] Response:', JSON.stringify(res.data));
          if (res.data) setMeterData(res.data);
        })
        .catch(err => console.error('[MeterPoll] Error:', err));
    };
    fetchMeter();
    const interval = setInterval(fetchMeter, 20000);
    return () => clearInterval(interval);
  }, [currentNodes]);

  // Poll WattelEM telemetry for devices assigned to Tableau (every 10s)
  useEffect(() => {
    const fetchTelemetry = () => {
      const sns: string[] = [];
      currentNodes
        .filter(n => n.type === 'tableau' && n.data?.wattelemList?.length > 0)
        .forEach(n => n.data.wattelemList.forEach((wem: any) => { if (wem.sn) sns.push(wem.sn); }));
      // Also include MQTT-linked equipment (point nodes with deviceSn but no deviceId)
      currentNodes
        .filter(n => n.type === 'point' && n.data?.deviceSn)
        .forEach(n => { if (n.data.deviceSn) sns.push(n.data.deviceSn); });
      if (sns.length === 0) return;
      const uniqueSns = [...new Set(sns)];
      wattelEMApi.getOverview()
        .then(devices => {
          const map: Record<string, TelemetryData> = {};
          for (const sn of uniqueSns) {
            const dev = devices.find(d => d.device_sn.toLowerCase() === sn.toLowerCase());
            if (dev) {
              const tel = dev.current;
              map[sn] = { ...tel, device_sn: dev.device_sn };
            }
          }
          setWattelemTelemetry(map);
        })
        .catch(() => {});
    };
    fetchTelemetry();
    const interval = setInterval(fetchTelemetry, 10000);
    return () => clearInterval(interval);
  }, [currentNodes]);


  // Load installation from URL ?load=ID or start fresh with ?new=true
  useEffect(() => {
    const isNew = searchParams.get('new');
    if (isNew) {
      // Fresh start: clear any saved session, ElectricalSchemaFlow will use its defaults
      sessionStorage.removeItem('currentInstallationId');
      setCurrentInstallationId(null);
      setCurrentInstallationName('');
      setCurrentInstallationDesc('');
      setCurrentNodes([]);
      setCurrentEdges([]);
      return;
    }

    if (loadId) {
      const id = parseInt(loadId, 10);
      if (isNaN(id)) return;
      setIsLoading(true);
      // Sauvegarder l'ID dans sessionStorage pour le recharger automatiquement
      sessionStorage.setItem('currentInstallationId', id.toString());
      architectureApi.getInstallation(id)
        .then(res => {
          const { nodes, edges } = installationToFlow(res.data, devices);
          setCurrentNodes(nodes);
          setCurrentEdges(edges);
          setCurrentInstallationId(res.data.id);
          setCurrentInstallationName(res.data.name);
          setCurrentInstallationDesc(res.data.description || '');
          setSaveMessage({ type: 'success', text: t('arch_toast_loaded', { name: res.data.name }) });
          setTimeout(() => setSaveMessage(null), 3000);
        })
        .catch(() => {
          setSaveMessage({ type: 'error', text: t('arch_toast_not_found') });
          setTimeout(() => setSaveMessage(null), 3000);
        })
        .finally(() => setIsLoading(false));
    } else {
      // Pas d'ID dans l'URL - essayer de charger depuis sessionStorage
      const savedId = sessionStorage.getItem('currentInstallationId');
      if (savedId) {
        const id = parseInt(savedId, 10);
        if (!isNaN(id)) {
          setIsLoading(true);
          architectureApi.getInstallation(id)
            .then(res => {
              const { nodes, edges } = installationToFlow(res.data, devices);
              setCurrentNodes(nodes);
              setCurrentEdges(edges);
              setCurrentInstallationId(res.data.id);
              setCurrentInstallationName(res.data.name);
              setCurrentInstallationDesc(res.data.description || '');
              console.log('[Architecture] Installation restaurée:', res.data.name);
            })
            .catch((err) => {
              console.error('[Architecture] Erreur restauration:', err);
              sessionStorage.removeItem('currentInstallationId');
            })
            .finally(() => setIsLoading(false));
        }
      }
    }
  }, [searchParams, devices]);

  // Handle installation selection from dropdown
  const handleSelectInstallation = (id: number) => {
    navigate(`/building-architecture?load=${id}&mode=${mode}`);
  };

  // Handle new installation creation from modal
  const handleCreateInstallation = () => {
    setShowNewInstallationModal(true);
  };

  // Handle view installation (consultation mode)
  const handleViewInstallation = (id: number) => {
    navigate(`/building-architecture?load=${id}&mode=view`);
  };

  // Handle edit installation
  const handleEditInstallation = (id: number) => {
    navigate(`/building-architecture?load=${id}&mode=edit`);
  };

  // Handle delete installation
  const handleDeleteInstallation = async (id: number) => {
    const inst = installations.find(i => i.id === id);
    if (!confirm(t('arch_confirm_delete', { name: inst?.name || id }))) return;
    try {
      await architectureApi.deleteInstallation(id);
      // Refresh list
      const listRes = await architectureApi.getInstallations();
      setInstallations(listRes.data || []);
      // If we deleted the current installation, reset
      if (currentInstallationId === id) {
        setCurrentInstallationId(null);
        setCurrentNodes([]);
        setCurrentEdges([]);
        setCurrentInstallationName('');
        setCurrentInstallationDesc('');
        sessionStorage.removeItem('currentInstallationId');
        navigate('/building-architecture?mode=view');
      }
      setSaveMessage({ type: 'success', text: t('arch_toast_deleted') });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (err) {
      console.error('[Architecture] Failed to delete:', err);
      setSaveMessage({ type: 'error', text: t('arch_err_delete') });
      setTimeout(() => setSaveMessage(null), 3000);
    }
  };

  const handleConfirmCreateInstallation = async (name: string, description: string) => {
    try {
      const res = await architectureApi.createInstallation({
        name,
        description,
        status: 'draft',
        zones: [],
      });
      // Refresh installations list
      const listRes = await architectureApi.getInstallations();
      setInstallations(listRes.data || []);
      setShowNewInstallationModal(false);
      // Navigate to the new installation in edit mode
      navigate(`/building-architecture?load=${res.data.id}&mode=edit`);
    } catch (err) {
      console.error('[Architecture] Failed to create installation:', err);
      setSaveMessage({ type: 'error', text: t('arch_err_create') });
      setTimeout(() => setSaveMessage(null), 3000);
    }
  };

  const handleNodeSelect = (node: Node | null) => {
    setSelectedNode(node);
  };

  const handleNodesEdgesChange = (nodes: Node[], edges: Edge[]) => {
    setCurrentNodes(nodes);
    setCurrentEdges(edges);
  };

  // Auto-save : sauvegarde automatique toutes les 3 secondes si une installation existe
  // Désactivé en mode consultation
  useEffect(() => {
    if (mode === 'view') return; // Pas d'auto-save en mode consultation
    if (!currentInstallationId || currentNodes.length === 0) return;
    
    const autoSaveTimer = setTimeout(async () => {
      const zones = flowToInstallationData(currentNodes, currentEdges);
      try {
        await architectureApi.updateInstallation(currentInstallationId, {
          name: currentInstallationName,
          description: currentInstallationDesc,
          zones,
        });
        console.log('[Architecture] Auto-sauvegarde effectuée');
      } catch (err: any) {
        console.error('[Architecture] Erreur auto-sauvegarde:', err);
        // Conflit d'exclusivité device : un SN est déjà utilisé ailleurs.
        // On le signale à l'utilisateur (l'auto-save n'a PAS enregistré).
        if (err?.message?.includes('déjà utilisé')) {
          setSaveMessage({ type: 'error', text: err.message });
          setTimeout(() => setSaveMessage(null), 6000);
          return;
        }
        // If the installation was deleted from DB (404 only — never on a 5xx, which would
        // silently duplicate the installation), reset and create a new one
        if (err?.status === 404 || err?.message?.includes('not found')) {
          console.log('[Architecture] Installation introuvable, création automatique...');
          try {
            const res = await architectureApi.createInstallation({
              name: currentInstallationName || 'Installation',
              description: currentInstallationDesc,
              status: 'draft',
              zones,
            });
            setCurrentInstallationId(res.data.id);
            sessionStorage.setItem('currentInstallationId', res.data.id.toString());
            console.log('[Architecture] Nouvelle installation créée:', res.data.id);
          } catch (createErr) {
            console.error('[Architecture] Erreur création:', createErr);
          }
        } else {
          // Toute autre erreur (ex : 500 côté serveur) : l'utilisateur doit savoir que
          // l'auto-save n'a PAS enregistré, avec le message réel du backend.
          setSaveMessage({ type: 'error', text: err?.message || t('arch_err_save') });
          setTimeout(() => setSaveMessage(null), 6000);
        }
      }
    }, 3000);

    return () => clearTimeout(autoSaveTimer);
  }, [mode, currentNodes, currentEdges, currentInstallationId, currentInstallationName, currentInstallationDesc]);

  const handleSaveInstallation = async () => {
    if (currentNodes.length === 0) {
      setSaveMessage({ type: 'error', text: t('arch_err_nothing_to_save') });
      setTimeout(() => setSaveMessage(null), 3000);
      return;
    }
    setShowSaveModal(true);
  };

  const handleConfirmSave = async (name: string, description: string) => {
    setIsSaving(true);
    setSaveMessage(null);

    try {
      const zones = flowToInstallationData(currentNodes, currentEdges);

      if (currentInstallationId) {
        await architectureApi.updateInstallation(currentInstallationId, {
          name,
          description,
          zones,
        });
        setCurrentInstallationName(name);
        setCurrentInstallationDesc(description);
        // Conserver l'ID dans sessionStorage
        sessionStorage.setItem('currentInstallationId', currentInstallationId.toString());
        setSaveMessage({ type: 'success', text: t('arch_toast_updated') });
      } else {
        const res = await architectureApi.createInstallation({
          name,
          description,
          status: 'draft',
          zones,
        });
        setCurrentInstallationId(res.data.id);
        setCurrentInstallationName(name);
        setCurrentInstallationDesc(description);
        // Conserver l'ID dans sessionStorage
        sessionStorage.setItem('currentInstallationId', res.data.id.toString());
        setSaveMessage({ type: 'success', text: t('arch_toast_saved') });
      }

      setShowSaveModal(false);
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error: any) {
      // Affiche le message réel du backend (ex : device déjà utilisé ailleurs)
      const msg = error?.message && !(error instanceof ApiStatusError)
        ? error.message
        : t('arch_err_save');
      setSaveMessage({ type: 'error', text: msg });
      setTimeout(() => setSaveMessage(null), 6000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddElement = (type: string, data: any) => {
    const newId = `${type}-${Date.now()}`;
    const newNode: Node = {
      id: newId,
      type: type === 'compteur' ? 'linky' : type,
      position: { 
        x: 250 + Math.random() * 200, 
        y: 150 + Math.random() * 200 
      },
      data: {
        ...data,
        status: type === 'zone' ? 'offline' : 'online',
        power: type === 'zone' ? 0 : type === 'point' ? 0 : undefined,
      },
    };

    setCurrentNodes(prev => [...prev, newNode]);
    // Don't force re-render with schemaKey - let ReactFlow handle it
    setSaveMessage({ type: 'success', text: t('arch_toast_added', { label: type === 'tableau' ? tableauDisplayName(data.label, t, tp) : displayDefaultName(data.label, t, tp) }) });
    setTimeout(() => setSaveMessage(null), 2000);
  };

  // Update a node's data (name, wattelemSn, power, etc.)
  const handleUpdateNodeData = (nodeId: string, updates: Record<string, any>) => {
    setCurrentNodes(prev => prev.map(n => {
      if (n.id !== nodeId) return n;
      const newData = { ...n.data, ...updates };
      const updatedNode = { ...n, data: newData };
      // Also update selectedNode so the panel reflects changes immediately
      setSelectedNode(updatedNode);
      return updatedNode;
    }));
    // Don't force re-render - ReactFlow will handle it
  };

  // Delete a node and its connected edges
  const handleDeleteNode = (nodeId: string) => {
    setCurrentNodes(prev => prev.filter(n => n.id !== nodeId));
    setCurrentEdges(prev => prev.filter(e => e.source !== nodeId && e.target !== nodeId));
    setSelectedNode(null);
    setSaveMessage({ type: 'success', text: t('arch_toast_element_deleted') });
    setTimeout(() => setSaveMessage(null), 2000);
  };

  // Keyboard shortcut: Delete key to remove selected node
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedNode) {
        // Don't delete if user is typing in an input field
        if (document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA') {
          return;
        }
        handleDeleteNode(selectedNode.id);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedNode]);

  const handleSelectTemplate = (nodes: Node[], edges: Edge[]) => {
    setCurrentNodes(nodes);
    setCurrentEdges(edges);
    // Force re-render only for templates (complete replacement)
    setSchemaKey(prev => prev + 1);
    setSaveMessage({ type: 'success', text: tp('arch_toast_template_applied', nodes.length) });
    setTimeout(() => setSaveMessage(null), 3000);
  };

  // Calcul des stats dynamiques
  const tableaux = currentNodes.filter(n => n.type === 'tableau');
  const zones = currentNodes.filter(n => n.type === 'zone');
  const points = currentNodes.filter(n => n.type === 'point');
  const onlineCount = currentNodes.filter(n => n.data?.status === 'online').length;
  const totalPower = zones.reduce((acc, z) => acc + (Number(z.data?.power) || 0), 0) + 
                     tableaux.reduce((acc, t) => acc + (Number(t.data?.power) || 0), 0);

  // Mode Consultation - Vue lecture seule avec beau design
  if (mode === 'view') {
    // Si le chargement est en cours, afficher le spinner
    if (isLoading) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-zce-navy via-slate-800 to-zce-navy flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin h-12 w-12 border-4 border-zce-blue border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-white text-lg">{t('arch_loading_installation')}</p>
          </div>
        </div>
      );
    }
    
    // Si aucune installation n'est chargée, afficher la ConsultationSchemaView 
    // avec le sélecteur (pas d'écran intermédiaire)
    return (
    <>
      <ConsultationSchemaView
        nodes={currentNodes}
        edges={currentEdges}
        devices={devices}
        catalogDevices={catalogDevices}
        meterData={meterData}
        wattelemTelemetry={wattelemTelemetry}
        installationName={currentInstallationName || t('arch_default_installation_name')}
        installations={installations}
        selectedInstallationId={currentInstallationId}
        onSelectInstallation={handleSelectInstallation}
        onCreateInstallation={canEdit ? handleCreateInstallation : undefined}
        onViewInstallation={handleViewInstallation}
        onEditInstallation={canEdit ? handleEditInstallation : undefined}
        onDeleteInstallation={canEdit ? handleDeleteInstallation : undefined}
        onSwitchToEdit={canEdit ? () => {
          const instId = currentInstallationId || loadId;
          if (instId) {
            navigate(`/building-architecture?load=${instId}&mode=edit`);
          } else {
            navigate(`/building-architecture?mode=edit`);
          }
        } : undefined}
        viewMode={viewMode}
        onChangeViewMode={changeViewMode}
        isVerticalView={isVerticalView}
        onToggleVerticalView={() => setIsVerticalView(!isVerticalView)}
        onWattelemClick={(sn) => {
          // Clic sur un WattelEM précis dans le tableau → équipement de CE WattelEM uniquement
          console.log('[Architecture] Clic WattelEM →', sn);
          navigate(`/wattel-em?sn=${encodeURIComponent(sn)}`);
        }}
        onTableauClick={(tableauNode) => {
          // Récupérer les SN des WattelEM associés à ce tableau
          const wattelemList = tableauNode.data?.wattelemList || [];
          const sns = wattelemList.map((wem: { sn?: string }) => wem.sn).filter(Boolean);
          console.log('[Architecture] Clic tableau → WattelEM avec SNs:', sns);
          
          if (sns.length > 0) {
            // Naviguer vers WattelEM avec tous les SNs (séparés par virgule)
            navigate(`/wattel-em?sn=${sns.join(',')}`);
          } else {
            // Pas de WattelEM assigné - aller sur la page WattelEM sans filtre
            navigate('/wattel-em');
          }
        }}
        onPointClick={(pointNode) => {
          const deviceId = pointNode.data?.deviceId;
          const deviceSn = pointNode.data?.deviceSn;
          const deviceLabel = pointNode.data?.label || t('eq_deviceLabel');
          const devicePower = pointNode.data?.power || 0;
          const deviceType = pointNode.data?.type || 'prise';
          const deducedEnergy = pointNode.data?.deducedEnergy || 0; // Wh, calculé depuis WattelEM
          const meterPower = pointNode.data?.meterPower || 0; // mW×10^-7 raw
          console.log('[Architecture] Clic point → Données complètes:', JSON.stringify(pointNode.data));
          console.log('[Architecture] Clic point → deviceId:', deviceId, 'deviceSn:', deviceSn, 'label:', deviceLabel);

          if (deviceId) {
            navigate(`/wattel-em?deviceId=${deviceId}`);
          } else if (deviceSn) {
            navigate(`/wattel-em?sn=${encodeURIComponent(deviceSn)}`);
          } else {
            const params = new URLSearchParams({
              pointName: deviceLabel,
              pointPower: devicePower.toString(),
              pointType: deviceType,
              pointDeducedEnergyWh: deducedEnergy.toFixed(4),
              pointMeterPower: meterPower.toString(),
            });
            navigate(`/wattel-em?${params.toString()}`);
          }
        }}
      />
      {showNewInstallationModal && (
        <SaveInstallationModal
          onSave={handleConfirmCreateInstallation}
          onClose={() => setShowNewInstallationModal(false)}
          defaultName=""
          defaultDescription=""
          isUpdate={false}
        />
      )}
    </>
    );
  }

  return (
    <div className="space-y-6">
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin h-8 w-8 border-4 border-zce-blue border-t-transparent rounded-full"></div>
          <span className="ml-3 text-zce-navy font-medium">{t('arch_loading_installation')}</span>
        </div>
      )}

      {/* ZCE Header - Hero animé premium */}
      <div className="relative overflow-hidden rounded-3xl shadow-2xl zce-fade-in">
        <div className="absolute inset-0 bg-gradient-to-br from-zce-navy-dark via-zce-navy to-zce-navy-light zce-gradient-anim" />
        <div className="pointer-events-none absolute -top-24 -left-24 w-72 h-72 rounded-full bg-cyan-500/20 blur-3xl zce-float-orb" />
        <div className="pointer-events-none absolute -bottom-32 -right-16 w-96 h-96 rounded-full bg-blue-500/20 blur-3xl zce-float-orb" style={{ animationDelay: '-6s' }} />
        <div className="pointer-events-none absolute top-1/2 left-1/3 w-64 h-64 rounded-full bg-emerald-500/10 blur-3xl zce-float-orb" style={{ animationDelay: '-12s' }} />
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        />
        <div className="pointer-events-none absolute inset-0 zce-shimmer" />

        <div className="relative p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4 zce-fade-up">
              <div className="relative">
                <div className="absolute inset-0 bg-cyan-400/40 rounded-xl blur-md zce-pulse-dot" />
                <img
                  src="/logos/logo zce.png"
                  alt={t('arch3_logo_alt')}
                  className="relative h-14 w-auto object-contain bg-white rounded-xl p-1.5 shadow-lg"
                />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-extrabold text-white font-heading tracking-tight leading-tight">
                  {t('arch_title_main')} <span className="bg-gradient-to-r from-cyan-300 via-blue-300 to-emerald-300 bg-clip-text text-transparent">{t('arch_title_accent')}</span>
                </h1>
                <p className="text-blue-200/80 text-sm mt-0.5">
                  {t('arch_subtitle')}
                </p>
              </div>
            </div>

            {/* Mode Indicator Badge */}
            <div className="flex items-center gap-3 flex-wrap zce-fade-up zce-delay-1">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-xl px-4 py-2 rounded-xl border border-white/20 transition-all hover:bg-white/15">
                <span className="w-2.5 h-2.5 bg-emerald-400 rounded-full zce-pulse-dot"></span>
                <span className="text-white font-medium text-sm">{t('arch_edit_mode')}</span>
              </div>
              {currentInstallationName && (
                <div className="bg-zce-orange/20 backdrop-blur-xl px-4 py-2 rounded-xl border border-zce-orange/30">
                  <span className="text-zce-orange font-semibold text-sm">{currentInstallationName}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Installation Selector Bar - Mode Édition */}
      <div className="bg-gradient-to-r from-slate-50 to-white rounded-xl border border-gray-200 px-6 py-2.5 shadow-sm">
        <div className="flex items-center justify-between flex-wrap gap-3">
          {/* Left: Select + New button */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-zce-navy" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <select
                value={currentInstallationId ?? ''}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val) handleSelectInstallation(Number(val));
                }}
                className="px-4 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-zce-navy bg-white focus:ring-2 focus:ring-zce-blue focus:border-zce-blue outline-none min-w-[250px] cursor-pointer"
              >
                <option value="">{t('arch_select_installation')}</option>
                {installations.map(inst => (
                  <option key={inst.id} value={inst.id}>
                    {inst.name} {inst.status === 'production' ? '🟢' : inst.status === 'validated' ? '🔵' : '⚪'}
                  </option>
                ))}
              </select>
            </div>
            <Btn variant="primary" size="sm" onClick={handleCreateInstallation} title={t('arch_new_installation')} icon={<IconPlus />}>
              <span className="hidden sm:inline">{t('arch_new_short')}</span>
            </Btn>
            <span className="text-xs text-gray-400 hidden md:inline">{t(installations.length === 1 ? 'arch_installations_count_one' : 'arch_installations_count_other', { n: installations.length })}</span>
          </div>

          {/* Right: Action buttons for selected installation */}
          {currentInstallationId && (
            <div className="flex items-center gap-2">
              <Btn size="sm" onClick={() => handleViewInstallation(currentInstallationId)} title={t('arch_view_installation')} icon={<IconEye />}>
                <span className="hidden sm:inline">{t('arch_view')}</span>
              </Btn>
              <Btn variant="danger" size="sm" onClick={() => handleDeleteInstallation(currentInstallationId)} title={t('arch_delete_installation')} icon={<IconTrash />}>
                <span className="hidden sm:inline">{t('arch_delete')}</span>
              </Btn>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons - Style ZCE */}
      <div className="flex flex-wrap items-center gap-2">
        <Btn variant="primary" onClick={handleSaveInstallation} disabled={isSaving}
          icon={isSaving ? (
            <svg className="animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden>
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          ) : <IconSave />}>
          {isSaving ? t('arch_saving') : t('arch_save')}
        </Btn>
        <Btn onClick={() => setShowTemplates(true)} icon={<IconTemplate />}>{t('arch_templates')}</Btn>
        {currentInstallationId && (
          <Btn onClick={() => { window.location.href = `/building-architecture?load=${currentInstallationId}&mode=view`; }} icon={<IconEye />}>
            {t('arch_view_mode')}
          </Btn>
        )}
        {/* Bascule portrait : tourne le schéma pour une tablette tenue verticalement */}
        <Btn
          active={isVerticalView}
          onClick={() => {
            setIsVerticalView(!isVerticalView);
            // Force re-render du schéma pour appliquer le nouveau layout
            setSchemaKey(prev => prev + 1);
          }}
          title={isVerticalView ? t('arch_landscape_tablet') : t('arch_portrait_tablet')}
          icon={<IconRotate />}
        >
          <span className="hidden sm:inline">{isVerticalView ? t('arch_landscape') : t('arch_portrait')}</span>
        </Btn>
      </div>

      {/* Save Message */}
      {saveMessage && (
        <div className={`p-4 rounded-xl flex items-center gap-3 border shadow-sm ${
          saveMessage.type === 'success' 
            ? 'bg-emerald-50 border-emerald-200 text-emerald-800' 
            : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          {saveMessage.type === 'success' ? <IconCircleCheck className="w-5 h-5 shrink-0" /> : <IconAlertTriangle className="w-5 h-5 shrink-0" />}
          <span className="font-medium">{saveMessage.text}</span>
        </div>
      )}

      {/* KPIs - Style ZCE moderne */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-zce border border-gray-100 p-5 hover:shadow-zce-md transition-shadow">
          <div className="flex items-center gap-4">
            <IconTile tone="amber"><IconBolt /></IconTile>
            <div>
              <p className="text-xs text-zce-gray-muted font-medium uppercase tracking-wider">{t('arch_kpi_total_power')}</p>
              <p className="text-2xl font-bold text-zce-navy font-heading">{totalPower.toFixed(1)} <span className="text-sm font-normal text-zce-gray-muted">kW</span></p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-zce border border-gray-100 p-5 hover:shadow-zce-md transition-shadow">
          <div className="flex items-center gap-4">
            <IconTile tone="blue"><IconZone /></IconTile>
            <div>
              <p className="text-xs text-zce-gray-muted font-medium uppercase tracking-wider">{t('arch_kpi_wattel_zones')}</p>
              <p className="text-2xl font-bold text-zce-navy font-heading">{zones.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-zce border border-gray-100 p-5 hover:shadow-zce-md transition-shadow">
          <div className="flex items-center gap-4">
            <IconTile tone="rose"><IconPlug /></IconTile>
            <div>
              <p className="text-xs text-zce-gray-muted font-medium uppercase tracking-wider">{t('arch_kpi_electrical_points')}</p>
              <p className="text-2xl font-bold text-zce-navy font-heading">{points.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-zce border border-gray-100 p-5 hover:shadow-zce-md transition-shadow">
          <div className="flex items-center gap-4">
            <IconTile tone="emerald"><IconCircleCheck /></IconTile>
            <div>
              <p className="text-xs text-zce-gray-muted font-medium uppercase tracking-wider">{t('arch_online')}</p>
              <p className="text-2xl font-bold text-emerald-600 font-heading">{onlineCount}<span className="text-lg text-zce-gray-muted">/{currentNodes.length}</span></p>
            </div>
          </div>
        </div>
      </div>

      {/* Schema Flow - Style ZCE */}
      <div className="bg-white rounded-2xl shadow-zce-md border border-gray-100 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-zce-gray-light to-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-zce-navy rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-zce-navy font-heading">
                {t('arch_schema_title')}
              </h2>
              <p className="text-xs text-zce-gray-muted">{t('arch_schema_hint')}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <LegendChip tone="amber">Linky</LegendChip>
            <LegendChip tone="slate">{t('arch_legend_tableau')}</LegendChip>
            <LegendChip tone="cyan">{t('arch_legend_zone')}</LegendChip>
            <LegendChip tone="rose">{t('arch_legend_point')}</LegendChip>
          </div>
        </div>
        
        <div className="p-4">
          <ElectricalSchemaFlow 
            key={schemaKey}
            onNodeSelect={handleNodeSelect}
            editable={true}
            onNodesEdgesChange={handleNodesEdgesChange}
            initialNodesOverride={currentNodes.length > 0 ? currentNodes : undefined}
            initialEdgesOverride={currentEdges.length > 0 ? currentEdges : undefined}
            devices={devices}
            catalogDevices={catalogDevices}
            ocppBornes={ocppBornes}
            meterData={meterData}
            wattelemTelemetry={wattelemTelemetry}
            isVerticalLayout={isVerticalView}
          />
        </div>
      </div>

      {/* Selected Node Editor - Style ZCE */}
      {selectedNode && (
        <div className="bg-white rounded-2xl shadow-zce-md border border-gray-100 overflow-hidden">
          <div className={`flex items-center justify-between px-6 py-4 border-b ${
            selectedNode.type === 'zone' ? 'bg-gradient-to-r from-cyan-50 to-white border-cyan-100' :
            selectedNode.type === 'point' ? 'bg-gradient-to-r from-pink-50 to-white border-pink-100' :
            selectedNode.type === 'linky' ? 'bg-gradient-to-r from-yellow-50 to-white border-yellow-100' :
            'bg-gradient-to-r from-gray-50 to-white border-gray-100'
          }`}>
            <div className="flex items-center gap-3">
              <IconTile size="sm" tone={selectedNode.type === 'zone' ? 'cyan' : selectedNode.type === 'point' ? 'rose' : selectedNode.type === 'linky' ? 'amber' : 'slate'}>
                {selectedNode.type === 'zone' ? <IconZone /> : selectedNode.type === 'point' ? <IconPlug /> : selectedNode.type === 'linky' ? <IconMeter /> : selectedNode.type === 'tableau' ? <IconPanel /> : <IconSliders />}
              </IconTile>
              <div>
                <h3 className="text-lg font-semibold text-zce-navy font-heading">
                  {selectedNode.type === 'zone' ? t('arch_zone_config') :
                   selectedNode.type === 'point' ? t('arch_point_config') :
                   t('arch_element_details')}
                </h3>
                <p className="text-xs text-zce-gray-muted">{selectedNode.type === 'tableau' ? tableauDisplayName(selectedNode.data?.label, t, tp) : (displayDefaultName(selectedNode.data?.label, t, tp) || t('arch_unnamed'))}</p>
              </div>
            </div>
            <button 
              onClick={() => setSelectedNode(null)}
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-colors"
              aria-label={t('arch_close')}
            >✕</button>
          </div>

          <div className="p-6">
          {/* ZONE EDITOR */}
          {selectedNode.type === 'zone' && (
            <div className="space-y-5">
              {/* Zone Name */}
              <div>
                <label className="block text-sm font-medium text-zce-navy mb-2">{t('arch_zone_name')}</label>
                <input
                  type="text"
                  value={selectedNode.data?.label || ''}
                  onChange={(e) => handleUpdateNodeData(selectedNode.id, { label: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none transition-all"
                />
              </div>

              {/* WATTELEM Device Selector */}
              <div>
                <label className="block text-sm font-medium text-zce-navy mb-2">
                  {t('arch_wattelem_device')}
                  <span className="text-xs text-zce-gray-muted ml-2 font-normal">{t('arch_available', { n: devices.length })}</span>
                </label>
                <select
                  value={selectedNode.data?.wattelemSn || ''}
                  onChange={(e) => {
                    const sn = e.target.value || undefined;
                    const device = sn ? devices.find(d => d.deviceSn === sn) : undefined;
                    handleUpdateNodeData(selectedNode.id, {
                      wattelemSn: sn,
                      power: device?.powerKw ?? 0,
                      status: device ? (device.online ? 'online' : 'offline') : 'offline',
                    });
                  }}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none bg-white transition-all"
                >
                  <option value="">{t('arch_select_wattelem')}</option>
                  {devices.map(d => {
                    const alreadyUsed = currentNodes.some(
                      n => n.type === 'zone' && n.id !== selectedNode.id && n.data?.wattelemSn === d.deviceSn
                    );
                    return (
                      <option key={d.deviceSn} value={d.deviceSn} disabled={alreadyUsed}>
                        {displaySn(d.deviceSn)} {d.online ? '🟢' : '🔴'} {d.powerKw ? `${d.powerKw} kW` : ''} {alreadyUsed ? t('arch_already_used') : ''}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* Live Device Info */}
              {selectedNode.data?.wattelemSn && (() => {
                const dev = devices.find(d => d.deviceSn === selectedNode.data?.wattelemSn);
                if (!dev) return null;
                return (
                  <div className={`p-3 rounded-lg border ${dev.online ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`w-2 h-2 rounded-full ${dev.online ? 'bg-green-500' : 'bg-red-500'}`}></span>
                      <span className="font-medium text-sm">{dev.online ? t('arch_online') : t('arch_offline')}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-3 text-sm">
                      <div>
                        <p className="text-gray-500">{t('arch_power')}</p>
                        <p className="font-semibold">{dev.powerKw ?? '-'} kW</p>
                      </div>
                      <div>
                        <p className="text-gray-500">{t('arch_voltage')}</p>
                        <p className="font-semibold">{dev.voltageV ?? '-'} V</p>
                      </div>
                      <div>
                        <p className="text-gray-500">{t('arch_current')}</p>
                        <p className="font-semibold">{dev.currentA ?? '-'} A</p>
                      </div>
                    </div>
                    {dev.lastSeen && (
                      <p className="text-xs text-gray-400 mt-2">
                        {t('arch_last_seen', { date: new Date(dev.lastSeen).toLocaleString(locale) })}
                      </p>
                    )}
                  </div>
                );
              })()}

              {!selectedNode.data?.wattelemSn && (
                <div className="p-3 rounded-lg bg-yellow-50 border border-yellow-200 text-sm text-yellow-800">
                  ⚠ {t('arch_no_wattelem_warning')}
                </div>
              )}
            </div>
          )}

          {/* POINT/EQUIPMENT EDITOR */}
          {selectedNode.type === 'point' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('arch_device_name')}</label>
                <input
                  type="text"
                  value={selectedNode.data?.label || ''}
                  onChange={(e) => handleUpdateNodeData(selectedNode.id, { label: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('arch_type')}</label>
                <select
                  value={selectedNode.data?.type || selectedNode.data?.pointType || ''}
                  onChange={(e) => handleUpdateNodeData(selectedNode.id, { type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500 outline-none bg-white"
                >
                  <option value="">{t('arch_type_placeholder')}</option>
                  <option value="prise">🔌 {t('arch_pt_prise')}</option>
                  <option value="lumiere">💡 {t('arch_pt_lumiere')}</option>
                  <option value="chauffage">🔥 {t('arch_pt_chauffage')}</option>
                  <option value="clim">❄️ {t('arch_pt_clim')}</option>
                  <option value="chauffeeau">🚿 {t('arch_pt_chauffeeau')}</option>
                  <option value="four">🍳 {t('arch_pt_four')}</option>
                  <option value="lave-linge">🧺 {t('arch_pt_lavelinge')}</option>
                  <option value="voiture">🚗 {t('arch_pt_voiture')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('arch_power_w')}</label>
                <input
                  type="number"
                  value={selectedNode.data?.power ?? ''}
                  onChange={(e) => handleUpdateNodeData(selectedNode.id, { power: e.target.value ? Number(e.target.value) : 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500 outline-none"
                  placeholder={t('arch_power_placeholder')}
                />
              </div>
            </div>
          )}

          {/* LINKY / TABLEAU read-only info */}
          {(selectedNode.type === 'linky' || selectedNode.type === 'tableau') && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">{t('arch_name')}</p>
                <p className="font-medium">{selectedNode.type === 'tableau' ? tableauDisplayName(selectedNode.data?.label, t, tp) : (displayDefaultName(selectedNode.data?.label, t, tp) || '-')}</p>
              </div>
              <div>
                <p className="text-sm text-zce-gray-muted">{t('arch_type')}</p>
                <p className="font-medium text-zce-navy capitalize">{selectedNode.type === 'tableau' ? t('arch_type_tableau') : selectedNode.type}</p>
              </div>
              {selectedNode.data?.power && (
                <div>
                  <p className="text-sm text-zce-gray-muted">{t('arch_power')}</p>
                  <p className="font-medium text-zce-navy">{selectedNode.data.power}</p>
                </div>
              )}
              {selectedNode.data?.modules && (
                <div>
                  <p className="text-sm text-zce-gray-muted">{t('arch_modules')}</p>
                  <p className="font-medium text-zce-navy">{selectedNode.data.modules}</p>
                </div>
              )}
            </div>
          )}

          {/* Delete node */}
          {(selectedNode.type === 'zone' || selectedNode.type === 'point' || selectedNode.type === 'linky' || selectedNode.type === 'tableau') && (
            <div className="mt-5 pt-5 border-t border-gray-100">
              <Btn variant="danger" onClick={() => handleDeleteNode(selectedNode.id)} icon={<IconTrash />}>{t('arch_delete_element')}</Btn>
            </div>
          )}
          </div>
        </div>
      )}

      {/* Instructions - Style ZCE */}
      <div className="bg-gradient-to-r from-zce-blue/10 to-blue-50 border border-zce-blue/20 rounded-2xl p-5">
        <div className="flex gap-4">
          <div className="w-12 h-12 bg-zce-blue rounded-xl flex items-center justify-center flex-shrink-0">
            <span className="text-2xl">💡</span>
          </div>
          <div>
            <h4 className="font-semibold text-zce-navy mb-2 font-heading">{t('arch_guide_title')}</h4>
            <ul className="text-sm text-zce-gray-text space-y-1.5">
              <li className="flex items-start gap-2">
                <span className="text-zce-blue">•</span>
                <span><strong className="text-zce-navy">{t('arch_guide_1_strong')}</strong> {t('arch_guide_1_rest')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-zce-blue">•</span>
                <span><strong className="text-zce-navy">{t('arch_guide_2_strong')}</strong> {t('arch_guide_2_rest')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-zce-blue">•</span>
                <span><strong className="text-zce-navy">{t('arch_guide_3_strong')}</strong> {t('arch_guide_3_rest')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-zce-blue">•</span>
                <span><strong className="text-zce-navy">{t('arch_guide_4_strong')}</strong> {t('arch_guide_4_rest')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-zce-blue">•</span>
                <span>{t('arch_guide_5_a')} <strong className="text-zce-navy">{t('arch_guide_5_wheel')}</strong> {t('arch_guide_5_b')} <strong className="text-zce-navy">{t('arch_guide_5_controls')}</strong> {t('arch_guide_5_c')}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Electrical Toolbox */}
      <ElectricalToolbox
        onAddElement={handleAddElement}
        isOpen={isToolboxOpen}
        onToggle={() => setIsToolboxOpen(!isToolboxOpen)}
      />

      {/* Templates Selector Modal */}
      {showTemplates && (
        <TemplatesSelector
          onSelectTemplate={handleSelectTemplate}
          onClose={() => setShowTemplates(false)}
        />
      )}

      {/* Save Installation Modal */}
      {showSaveModal && (
        <SaveInstallationModal
          onSave={handleConfirmSave}
          onClose={() => setShowSaveModal(false)}
          defaultName={currentInstallationName}
          defaultDescription={currentInstallationDesc}
          isUpdate={!!currentInstallationId}
        />
      )}

      {/* New Installation Modal (from selector) */}
      {showNewInstallationModal && (
        <SaveInstallationModal
          onSave={handleConfirmCreateInstallation}
          onClose={() => setShowNewInstallationModal(false)}
          defaultName=""
          defaultDescription=""
          isUpdate={false}
        />
      )}
    </div>
  );
}

export default BuildingArchitecturePage;
